import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'
import { Icon, IconChoices } from '../../../../components/common/Icon'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useCallbackDebugger } from '../../../../hooks/useEffectAndCallbackDebugger'
import { societyHiddenWidgetsSchema } from '../../../../forms/schemas/society_hidden_widgets'
import { NSocieties } from '../../../../interfaces/services/societies.interfaces'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { FunctionSettingsRow } from '../../../../components/society/FunctionSettings/FunctionSettingsRow'
import { patchSociety } from '../../../../api/societies'
import { i18nSocietyType } from '../../../../i18n'
import { theme } from '../../../../theme/theme'
import { Toggle } from '../../../../components/common/Toggle/Toggle'
import { useFeatures } from '../../../../hooks/useFeatures'

export const FunctionSettingsViewForm = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()
  const { enabledFeatures } = useFeatures()

  const hiddenWidgets = society?.hiddenSocietyWidgets

  const getDefaultValues = useCallbackDebugger((): InferType<
    typeof societyHiddenWidgetsSchema
  > => {
    return {
      'ticket-question': !hiddenWidgets?.includes('ticket-question'),
      'ticket-report': !hiddenWidgets?.includes('ticket-report'),
      'resident-list': !hiddenWidgets?.includes('resident-list'),
      board: !hiddenWidgets?.includes('board'),
      contacts: !hiddenWidgets?.includes('contacts'),
      calendar: !hiddenWidgets?.includes('calendar'),
      documents: !hiddenWidgets?.includes('documents'),
      faqs: !hiddenWidgets?.includes('faqs'),
      facilities: !hiddenWidgets?.includes('facilities'),
      rules: !hiddenWidgets?.includes('rules'),
      reportProblem: !hiddenWidgets?.includes('reportProblem'),
      'local-offers': !hiddenWidgets?.includes('local-offers'),
      sales: !hiddenWidgets?.includes('sales'),
      marketplace: !hiddenWidgets?.includes('marketplace'),
      services: !hiddenWidgets?.includes('services'),
      'find-friends': !hiddenWidgets?.includes('find-friends'),
    }
  }, [hiddenWidgets])

  const { control, watch } = useForm({
    resolver: yupResolver(societyHiddenWidgetsSchema),
    defaultValues: getDefaultValues(),
  })

  const { society: selectedSociety } = useCurrentSociety()

  const { mutate: mutatePatchSociety } = useMutation({
    mutationFn: patchSociety,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('flashMessage.changesSaved')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
  })
  useEffect(() => {
    const subscription = watch(async (data) => {
      const societyId = selectedSociety?._id

      if (!societyId) {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongError')
        )
        return
      }

      const hiddenSocietyWidgets = Object.keys(data).filter((key) => !data[key])
      const formData: NSocieties.NPatch.IRequestBody = {
        hiddenSocietyWidgets,
      }

      mutatePatchSociety({ id: societyId, body: formData })
    })
    return () => subscription.unsubscribe()
  }, [
    mutatePatchSociety,
    selectedSociety?._id,
    watch,
    setToastNotification,
    translate,
  ])

  return (
    <div className="space-y-2">
      {enabledFeatures.ticket && (
        <div className="flex flex-col gap-2">
          <div className="flex rounded bg-neutral-96 px-4 py-2 w-full items-center">
            <div className="rounded-full bg-white p-2 self-start">
              <Icon icon={IconChoices.ASSIGNMENT} />
            </div>
            <p className="ml-2 mr-4" style={theme.textVariants.baseBold}>
              {translate('ticketView.title')}
            </p>
          </div>
          <div className="flex flex-col gap-2 pl-10">
            <div className="flex rounded bg-neutral-96 px-4 py-2 w-full items-center justify-between">
              <p className="ml-2 mr-4" style={theme.textVariants.baseBold}>
                {translate('ticketView.form.tabs.question')}
              </p>
              <Controller
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <Toggle name={name} enabled={value} onChange={onChange} />
                )}
                name="ticket-question"
              />
            </div>
            <div className="flex rounded bg-neutral-96 px-4 py-2 items-center justify-between">
              <p className="ml-2 mr-4" style={theme.textVariants.baseBold}>
                {translate('ticketView.form.tabs.report')}
              </p>
              <Controller
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <Toggle name={name} enabled={value} onChange={onChange} />
                )}
                name="ticket-report"
              />
            </div>
          </div>
        </div>
      )}
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('residentListView.title')}
            name={name}
            icon={IconChoices.RESIDENT_LIST}
            value={value}
            onChange={onChange}
          />
        )}
        name="resident-list"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('boardListView.title', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
            name={name}
            icon={IconChoices.BOARD}
            value={value}
            onChange={onChange}
          />
        )}
        name="board"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('contactsView.title')}
            name={name}
            icon={IconChoices.GROUP}
            value={value}
            onChange={onChange}
          />
        )}
        name="contacts"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('societyCalendarListView.title')}
            name={name}
            icon={IconChoices.CALENDAR}
            value={value}
            onChange={onChange}
          />
        )}
        name="calendar"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('documentsView.title', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
            name={name}
            icon={IconChoices.FOLDER}
            value={value}
            onChange={onChange}
          />
        )}
        name="documents"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('facilitiesView.title')}
            name={name}
            icon={IconChoices.LAUNDRY}
            value={value}
            onChange={onChange}
          />
        )}
        name="facilities"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('questionsAndAnswersView.title')}
            name={name}
            icon={IconChoices.QUESTION_MARK}
            value={value}
            onChange={onChange}
          />
        )}
        name="faqs"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('houseRulesView.title')}
            name={name}
            icon={IconChoices.LIST}
            value={value}
            onChange={onChange}
          />
        )}
        name="rules"
      />

      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('localOffersView.title')}
            name={name}
            icon={IconChoices.LOCAL_OFFER}
            value={value}
            onChange={onChange}
          />
        )}
        name="local-offers"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('reportProblemView.title')}
            name={name}
            icon={IconChoices.REPORT}
            value={value}
            onChange={onChange}
          />
        )}
        name="reportProblem"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('marketplaceListView.title')}
            name={name}
            icon={IconChoices.STORE}
            value={value}
            onChange={onChange}
          />
        )}
        name="marketplace"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FunctionSettingsRow
            label={translate('findFriendsView.title')}
            name={name}
            icon={IconChoices.GROUP}
            value={value}
            onChange={onChange}
          />
        )}
        name="find-friends"
      />
    </div>
  )
}
